import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import {
    emptySurveyQuestionResponse,
    RESPONSE_OPTION_MAX_LENGTH,
} from "utils/consts";
import { swapPositions } from "utils/swap-positions";
import { IconButton, Button, Tooltip } from "@material-tailwind/react";
import MaterialIcon from "components/material-icon";
import RHFTextarea from "./rhf-textarea";

// P4 child: owns the per-question preset_answers useFieldArray. Rendered
// from QuestionsPanel's QuestionCard, so the nested array hooks never sit
// inside a .map() callback (Decision 3).
export default function QuestionAnswerInputs({
    questionIndex,
    isReadOnly,
}) {
    const { control, getValues } = useFormContext();
    const answersPath = `questions.${questionIndex}.preset_answers`;
    const { fields: answerFields, append, remove, swap } = useFieldArray({
        name: answersPath,
        control,
    });

    const addResponse = () => {
        const newResponse = Object.assign({}, emptySurveyQuestionResponse);
        const currentAnswers = getValues(answersPath);
        const pos =
            currentAnswers.length === 0
                ? 1
                : currentAnswers.length + 1;
        newResponse.position = pos;
        append(newResponse);
    };

    // Position bookkeeping: the position values mutate in place on the
    // live store rows, then the field array's swap re-derives dirty state
    // from the mutated positions.
    const moveResponse = (index, delta) => {
        const answers = getValues(answersPath);
        swapPositions({
            readPosition: (i) => answers[i].position,
            writePosition: (i, position) => {
                answers[i].position = position;
            },
            swap,
            index,
            delta,
        });
    };

    return (
        <>
            <div className="text-xs text-gray-500 mt-2 ml-4">
                Response options have a maximum of {RESPONSE_OPTION_MAX_LENGTH} characters
            </div>
            <div className="flex flex-col gap-4 p-4 sm:rounded-2xl rounded-xl mt-2">
                {answerFields
                    .map((field, index) => ({ field, index }))
                    .sort((a, b) => {
                        return a.field.position - b.field.position;
                    })
                    .map(({ field, index }) => (
                        <div className="flex gap-2 items-center" key={field.nanoid || field.id}>
                            <RHFTextarea
                                name={`questions.${questionIndex}.preset_answers.${index}.text`}
                                className="w-full font-body"
                                minRows={1}
                                autoComplete="off"
                                disabled={isReadOnly}
                                maxLength={RESPONSE_OPTION_MAX_LENGTH}
                                showCounter={true}
                            />
                            <IconButton
                                className="btn-icon--secondary"
                                type="button"
                                variant="text"
                                disabled={index === 0 || isReadOnly}
                                onClick={() => moveResponse(index, -1)}
                            >
                                <MaterialIcon name="arrow_upward" size="xl" />
                            </IconButton>
                            <IconButton
                                className="btn-icon--secondary"
                                type="button"
                                variant="text"
                                disabled={
                                    index ===
                                        answerFields.length - 1 || isReadOnly
                                }
                                onClick={() => moveResponse(index, 1)}
                            >
                                <MaterialIcon name="arrow_downward" size="xl" />
                            </IconButton>
                            <IconButton
                                className="btn-icon--danger"
                                type="button"
                                variant="text"
                                onClick={() => remove(index)}
                                disabled={isReadOnly}
                            >
                                <MaterialIcon name="delete" size="xl" />
                            </IconButton>
                        </div>
                    ))}
            </div>
            {!!(
                getValues(`questions.${questionIndex}.question_type`) !== "freeform"
            ) && (
                <div className="flex justify-center">
                    <div>
                        <Tooltip
                            className="font-body"
                            animate={{
                                mount: { scale: 1, y: 0 },
                                unmount: { scale: 0, y: 25 },
                            }}
                            content={`Add a single or multiple response option. Response options have a maximum of ${RESPONSE_OPTION_MAX_LENGTH} characters.`}
                        >
                            <Button
                                variant="filled"
                                className="btn btn--primary"
                                type="button"
                                onClick={addResponse}
                                disabled={isReadOnly}
                            >
                                + Add A Response
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            )}
        </>
    );
}
